// src/services/BaseApiService.js

import axios from 'axios';
import { showGlobalErrorDialog, buildErrorDialogData } from '../../providers/ErrorDialogProvider';
import { trackException } from '../api/TrackingService';

// APIの基本設定
const API_BASE_URL = process.env.REACT_APP_API_URL;

// セッション切れ時の重複リダイレクト防止フラグ
let hasRedirected = false;

// CSRFトークン管理
let csrfToken = null;
let csrfTokenExpiry = null;

// エラーID生成ユーティリティ
const generateErrorId = () => {
  return `API_ERR_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

// エラーログ送信（バックエンドAPI経由）
const doTrackingError = (error, context = {}) => {  
  const { config } = error;

  // エラーIDを生成
  const errorId = generateErrorId();

  // トラッキング設定の取得
  const tracking = config?.tracking || {};

  // エラー情報をトラッキングしない場合はエラーIDを返して終了
  if (!tracking.errors)
    return errorId;

  try {
    // バックエンドAPIを使用して例外を追跡
    trackException(error, {
      errorId,
      source: 'BaseService',
      ...context,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      apiBaseUrl: API_BASE_URL
    }).catch(loggingError => {
      console.error('エラーログ送信に失敗:', loggingError);
    });
  } catch (loggingError) {
    console.error('エラーログ送信処理中にエラーが発生:', loggingError);
  }
  
  return errorId;
};

// CSRFトークン管理機能（Fetch版）
class CSRFManager {
  /**
   * CSRFトークンを取得する
   * @returns {Promise<string>} CSRFトークン
   */
  static async getCSRFToken() {
    try {
      // トークンが有効期限内にある場合は既存のトークンを返す
      if (csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
        console.log('既存のCSRFトークンを使用');
        return csrfToken;
      }

      console.log('新しいCSRFトークンを取得中...');
      
      // 新しいCSRFトークンを取得
      const response = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include', // セッションクッキーを含める
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`CSRFトークンの取得に失敗: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // トークンと有効期限を保存
      csrfToken = data.csrf_token;
      csrfTokenExpiry = Date.now() + (data.expires_in * 1000) - (60 * 1000); // 1分早めに期限切れとして扱う
      
      console.log('CSRFトークンを取得しました', {
        expiresIn: data.expires_in,
        expiryTime: new Date(csrfTokenExpiry).toLocaleString()
      });

      return csrfToken;
    } catch (error) {
      console.error('CSRFトークンの取得中にエラーが発生:', error);
      throw error;
    }
  }

  /**
   * CSRFトークンをクリアする
   */
  static clearCSRFToken() {
    csrfToken = null;
    csrfTokenExpiry = null;
    console.log('CSRFトークンをクリアしました');
  }

  /**
   * CSRFトークンが期限切れかチェック
   * @returns {boolean} 期限切れの場合true
   */
  static isCSRFTokenExpired() {
    return !csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry;
  }
}

/**
 * セッション切れ（401）検知時の共通後処理。
 * axiosベースのBaseApiServiceだけでなく、fetchを直接使うサービス（AiChatServiceのSSE通信等）からも
 * 同じログアウト・リダイレクト挙動に揃えるために再利用可能な関数として切り出している。
 * @param {Object} error - { response: { status, data }, config: { url, method } } 相当のエラー情報
 * @param {Object} [context] - buildErrorDialogDataへ渡す追加情報（errorId/durationなど）
 */
export const handleSessionExpiredResponse = (error, context = {}) => {
  // セッションタイムアウト時はCSRFトークンもクリア
  CSRFManager.clearCSRFToken();

  // トークン／ユーザ情報をクリア
  localStorage.removeItem('token');
  localStorage.removeItem('userInfo');

  // 一度だけログイン画面へリダイレクト
  if (hasRedirected) return;

  // バックエンドレスポンスのトレース用にエラー詳細をログ出力
  console.error(`API Error: ${error.response?.status}`, {
    errorId: context.errorId,
    responseData: error.response?.data,
  });

  hasRedirected = true;

  // セッション期限切れイベントをディスパッチ（SearchHistory等で使用）
  window.dispatchEvent(new Event('sessionExpired'));

  // セッション期限切れダイアログを表示（showErrorDialogフラグは無視）
  const dialogData = buildErrorDialogData(error, {
    ...context,
    customConfig: {
      title: 'セッションタイムアウト',
      className: 'error-dialog--session',
      // タイムアウトと多重ログインによるセッション終了時に同じメッセージを表示する
      message: 'セッションが終了しました。再度ログインしてください。',
      // ダイアログを閉じた後にログイン画面へリダイレクト
      onCustomClose: () => {
        window.location.href = `/`;
      }
    }
  });

  showGlobalErrorDialog(dialogData);
};

// デフォルトのトラッキング設定
const defaultTrackingSettings = {
  errors: true,    // エラートラッキングが有効
};

// axiosインスタンスの作成
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30秒（デフォルト）
  withCredentials: true, // CSRFセッション用
  tracking: defaultTrackingSettings // デフォルトのトラッキング設定
});

// リクエストインターセプター
apiClient.interceptors.request.use(
  async config => {
    // リクエスト開始時刻を記録
    config.metadata = { startTime: Date.now() };

    // トークンの取得 (localStorageから)
    const token = localStorage.getItem('token');
    // トークンがあればヘッダーに追加
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // CSRFトークンが必要なリクエストの場合に追加
    if (config.requireCSRF) {
      try {
        const csrf = await CSRFManager.getCSRFToken();
        config.headers['X-CSRF-Token'] = csrf;
      } catch (error) {
        // CSRFトークンが必須の場合はリクエストを中止
        return Promise.reject(new Error('CSRF対策のため、リクエストを中止しました'));
      }
    }

    // FormDataの場合はContent-Typeを自動設定させる
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  error => {
    
    // エラーをトラッキング
    const errorId = doTrackingError(error, {
      stage: 'request_interceptor',
      errorType: 'request_setup_error'
    });
    
    console.error('リクエストインターセプターエラー:', { errorId, error });
    
    // エラーダイアログを表示
    const dialogData = buildErrorDialogData(error, {
      errorId,
      // ※ suggetionという属性は使われていない
      suggestion: 'リクエストの設定に問題があります。ページをリフレッシュして再度お試しください。'
    });
    showGlobalErrorDialog(dialogData);
    
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
apiClient.interceptors.response.use(
  response => {
    // X-New-Tokenヘッダーがある場合はトークンを更新
    const newToken = response.headers['x-new-token'];
    if (newToken) {
      localStorage.setItem('token', newToken);
      console.log('トークンが更新されました');
    }

    // BLOBレスポンスの場合は、データをそのまま返す
    if (response.config.responseType === 'blob') {
      return response;
    }

    // 通常のレスポンスの場合は、dataのみを返す
    return response.data;
  },
  async error => {
    const { response, config } = error;
    const duration = Date.now() - (config?.metadata?.startTime || 0);
    const errorId = generateErrorId();

    // BLOBレスポンスの場合に、レスポンスデータを解析する処理（CSVダウンロード機能など）
    // バックエンド側で返されたエラーレスポンスに含まれる
    // メッセージ項目（detail）を表示する
    if (config?.responseType === 'blob' && response?.data instanceof Blob) {
      try {
        const text = await response.data.text();
        try {
          const jsonData = JSON.parse(text);
          // レスポンスデータをJSONオブジェクトに置き換え
          response.data = jsonData;
        } catch (jsonParseError) {
          // JSONでない場合はテキストとして保持
          response.data = { message: text };
        }
      } catch (textError) {
        response.data = { message: 'レスポンスの読み取りに失敗しました' };
      }
    }

    // エラーレスポンスでもX-New-Tokenをチェック
    if (response && response.headers && response.headers['x-new-token']) {
      const newToken = response.headers['x-new-token'];
      localStorage.setItem('token', newToken);
      console.log('エラー発生中もトークンが更新されました');
    }

    // CSRF関連のエラー処理
    if (response && response.status === 403) {
      const errorDetail = response.data?.detail || '';

      // CSRFトークン関連のエラーの場合
      if (errorDetail.includes('CSRF') || errorDetail.includes('csrf')) {
        console.warn('CSRFトークンエラーが発生、トークンをクリア:', errorDetail);
        CSRFManager.clearCSRFToken();
        
        // CSRFエラー用のダイアログを表示
        const dialogData = buildErrorDialogData(error, {
          errorId,
          duration,
          method: config?.method,
          customConfig: {
            title: 'セキュリティエラー',
            className: 'error-dialog--csrf'
          },
          // ※ suggetionという属性は使われていない
          suggestion: 'セキュリティ保護のため、操作を再度お試しください。'
        });
        showGlobalErrorDialog(dialogData);
        return Promise.reject(error);
      }
      else if (String(response?.data || '').includes('Service unavailable') && String(response?.data || '').includes('The request is blocked.') && String(response?.data || '').includes('errorref')) {
        console.warn('WAFブロックを検知:', errorDetail);
        CSRFManager.clearCSRFToken();

        const dialogData = buildErrorDialogData(error, {
          errorId,
          duration,
          method: config?.method,
          customConfig: {
            title: 'セキュリティエラー',
            className: 'error-dialog--waf',
            // ※ messageを指定することで指定したメッセージを表示するエラーダイアログが作成される
            message: '送信された内容にセキュリティ上の問題が検出されました。入力内容をご確認の上、再度お試しください。'
            },
        });
        showGlobalErrorDialog(dialogData);
        return Promise.reject(error);
      }
    }


    // ログイン失敗（POST /login の 401）
    if (response && response.status === 401 && config?.url?.includes('/login')) {
      // ログイン失敗時はCSRFトークンをクリア
      CSRFManager.clearCSRFToken();

      // ログインエラー用のダイアログを表示（showErrorDialogフラグは無視）
      const dialogData = buildErrorDialogData(error, {
        errorId,
        duration,
        method: config?.method,
        customConfig: {
          title: 'ログインエラー',
          className: 'error-dialog--login'
        },
        // ※ suggetionという属性は使われていない
        suggestion: 'アカウント名またはパスワードが正しくありません。入力内容を確認して再度お試しください。'
      });
      
      showGlobalErrorDialog(dialogData);
      
      return Promise.reject(error);
    }

    // セッションタイムアウト検知 (401)
    if (response && response.status === 401) {
      handleSessionExpiredResponse(error, { errorId, duration });

      // 以降の in-flight リクエストをキャンセル
      const cancelSource = axios.CancelToken.source();
      apiClient.defaults.cancelToken = cancelSource.token;
      cancelSource.cancel('SessionTimeout');

      // 後続の .then/.catch を実行させない（画面チラつき防止）
      return new Promise(() => {});
    }

    // showErrorDialogフラグがfalseの場合はエラーダイアログを表示しない
    let showErrorDialog = config?.showErrorDialog !== false;

    doTrackingError(error, {
      errorType: response ? 'http_error' : 'network_error',
      endpoint: config?.url,
      statusCode: response?.status,
      responseData: response?.data,
      showErrorDialog
    });

    // エラーダイアログ表示
    if (showErrorDialog) {
      const dialogData = buildErrorDialogData(error, {
        errorId,
        duration,
        method: config?.method
      });
      showGlobalErrorDialog(dialogData);
    }

    // エラー詳細をログ出力
    if (response) {
      console.error(`API Error: ${response.status}`, { 
        errorId, 
        responseData: response.data,
        showErrorDialog
      });
    } else if (error.request) {
      console.error('No response received', { errorId, request: error.request });
    } else {
      console.error('Request setup error', { errorId, message: error.message });
    }

    return Promise.reject(error);
  }
);

// X-New-Token を fetch からも取得するためのユーティリティ
const handleNewToken = async response => {
  if (response && response.headers) {
    const newToken = response.headers.get('x-new-token');
    if (newToken) {
      localStorage.setItem('token', newToken);
      console.log('PDF取得中にトークンが更新されました');      
    }
  }
};

// ApiService クラス
class ApiService {
  // GETリクエスト
  static async get(endpoint, params = {}, config = {}) {
    const startTime = Date.now();
    try {
      const result = await apiClient.get(endpoint, { params, ...config });
      return result;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'GET', 
        endpoint, 
        params,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      // エラーにIDを追加
      error.errorId = errorId;
      throw error;
    }
  }

  // POSTリクエスト（ログイン用）
  static async postWithCSRF(endpoint, data = {}, config = {}) {
    const startTime = Date.now();
    try {
      // CSRF必須フラグを設定
      const finalConfig = { 
        ...this.adjustTimeoutForFormData(data, config),
        requireCSRF: true 
      };
      const result = await apiClient.post(endpoint, data, finalConfig);
      return result;
    } catch (error) {
      throw error;
    }
  }

  // 通常のPOSTリクエスト
  static async post(endpoint, data = {}, config = {}) {
    const startTime = Date.now();
    try {
      // FormDataかつファイルサイズが大きい場合はタイムアウトを調整
      const finalConfig = this.adjustTimeoutForFormData(data, config);
      
      // デバッグログ
      if (data instanceof FormData) {
        console.log(`FormData POST: ${endpoint}`, {
          timeout: finalConfig.timeout,
          hasFiles: this.hasFiles(data)
        });
      }

      const result = await apiClient.post(endpoint, data, finalConfig);
      return result;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'POST', 
        endpoint, 
        hasFormData: data instanceof FormData,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // CSRFトークン取得用のヘルパーメソッド
  static async getCSRFToken() {
    return await CSRFManager.getCSRFToken();
  }

  // CSRFトークンクリア用のヘルパーメソッド
  static clearCSRFToken() {
    CSRFManager.clearCSRFToken();
  }

  // PUTリクエスト
  static async put(endpoint, data = {}, config = {}) {
    const startTime = Date.now();
    try {
      const finalConfig = this.adjustTimeoutForFormData(data, config);
      const result = await apiClient.put(endpoint, data, finalConfig);
      return result;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'PUT', 
        endpoint,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // DELETEリクエスト
  static async delete(endpoint, config = {}) {
    const startTime = Date.now();
    try {
      const result = await apiClient.delete(endpoint, config);
      return result;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'DELETE', 
        endpoint,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // PATCHリクエスト
  static async patch(endpoint, data = {}, config = {}) {
    const startTime = Date.now();
    try {
      const finalConfig = this.adjustTimeoutForFormData(data, config);
      const result = await apiClient.patch(endpoint, data, finalConfig);
      return result;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'PATCH', 
        endpoint,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // BLOB取得用のGETリクエスト
  static async getBlob(endpoint, params = {}, filename = '', config = {}) {
    const startTime = Date.now();
    try {
      const response = await apiClient.get(endpoint, {
        params,
        responseType: 'blob',
        ...config
      });

      if (filename) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      return response;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'GET_BLOB', 
        endpoint, 
        filename,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // PDF取得用のGETリクエスト（fetch使用）
  static async getPdf(endpoint, params = {}, config = {}) {
    const startTime = Date.now();
    try {
      const url = endpoint.startsWith('http')
        ? endpoint
        : `${API_BASE_URL}${endpoint}`
         + (Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '');

      console.log(`PDFを取得中: ${url}`, { params });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf, application/octet-stream',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        credentials: 'include'
      });

      // トークン更新チェック
      await handleNewToken(response);

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        console.warn(
          `getPdf: HTMLレスポンス（${contentType}）を検出。ダウンロードを中止します。`
        );
        return null;
      }

      if (!response.ok) {
        // fetchでのエラーレスポンス処理
        let errorData = null;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorData = await response.text();
          }
        } catch (parseError) {
          errorData = `HTTP ${response.status} ${response.statusText}`;
        }

        const error = new Error(`PDFの取得に失敗しました: ${response.status} ${response.statusText}`);
        error.response = { 
          status: response.status, 
          statusText: response.statusText,
          data: errorData
        };
        
        // PDFエラー用のダイアログデータ（showErrorDialogが設定されていなければ表示）
        if (!config.showErrorDialog) {
          const errorId = generateErrorId();
          const dialogData = buildErrorDialogData(error, {
            errorId,
            duration: Date.now() - startTime,
            method: 'GET_PDF',
            // ※ suggetionという属性は使われていない
            suggestion: 'PDFファイルが存在しないか、アクセス権限がない可能性があります。'
          });
          showGlobalErrorDialog(dialogData);
        }
        
        throw error;
      }

      const data = await response.arrayBuffer();
      if (!data || data.byteLength === 0) {
        const error = new Error('PDFデータが空です');
        const errorId = generateErrorId();
        if (!config.showErrorDialog) {
          const dialogData = buildErrorDialogData(error, {
            errorId,
            duration: Date.now() - startTime,
            method: 'GET_PDF',
            // ※ suggetionという属性は使われていない
            suggestion: 'PDFファイルが空か、正しく生成されていない可能性があります。'
          });
          showGlobalErrorDialog(dialogData);
        }
        
        throw error;
      }
      
      console.log(`PDFデータを取得しました (${data.byteLength} バイト)`);
      return data;
    } catch (error) {
      if (!error.errorId) {
        const errorId = generateErrorId();

        console.error('PDFの取得中にエラーが発生しました:', { errorId, error });
        error.errorId = errorId;
        
        if (!error.response && !config.showErrorDialog) {
          const dialogData = buildErrorDialogData(error, {
            errorId,
            duration: Date.now() - startTime,
            method: 'GET_PDF'
          });
          showGlobalErrorDialog(dialogData);
        }
      }
      throw error;
    }
  }

  // FormDataのタイムアウト調整
  static adjustTimeoutForFormData(data, config = {}) {
    // FormDataでない場合はそのまま返す
    if (!(data instanceof FormData)) {
      return config;
    }

    // 既にタイムアウトが設定されている場合はそれを優先
    if (config.timeout) {
      return config;
    }

    // ファイルサイズを推定してタイムアウトを調整
    const estimatedSize = this.estimateFormDataSize(data);
    const adjustedTimeout = this.calculateTimeout(estimatedSize);

    console.log(`FormData タイムアウト調整: ${estimatedSize}bytes → ${adjustedTimeout}ms`);

    return {
      ...config,
      timeout: adjustedTimeout
    };
  }

  // FormDataのサイズを推定
  static estimateFormDataSize(formData) {
    let totalSize = 0;
    
    try {
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          totalSize += value.size;
        } else if (typeof value === 'string') {
          totalSize += new Blob([value]).size;
        }
      }
    } catch (error) {
      console.warn('FormDataサイズ推定に失敗:', error);
      return 50 * 1024 * 1024; // 50MBと仮定
    }

    return totalSize;
  }

  // ファイルサイズに基づいてタイムアウト時間を計算
  static calculateTimeout(sizeInBytes) {
    const baseTimeout = 30000; // 30秒（デフォルト）
    const maxTimeout = 300000; // 5分（最大）

    if (sizeInBytes <= 5 * 1024 * 1024) { // 5MB以下
      return baseTimeout;
    } else if (sizeInBytes <= 20 * 1024 * 1024) { // 20MB以下
      return 90000; // 1.5分
    } else if (sizeInBytes <= 50 * 1024 * 1024) { // 50MB以下
      return 120000; // 2分
    } else if (sizeInBytes <= 100 * 1024 * 1024) { // 100MB以下
      return 180000; // 3分
    } else {
      return maxTimeout; // 5分
    }
  }

  // FormDataにファイルが含まれているかチェック
  static hasFiles(formData) {
    if (!(formData instanceof FormData)) {
      return false;
    }

    try {
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          return true;
        }
      }
    } catch (error) {
      console.warn('FormDataのファイルチェックに失敗:', error);
    }

    return false;
  }

  // プログレス監視付きPOST
  static async postWithProgress(endpoint, data = {}, config = {}, onProgress = null) {
    const startTime = Date.now();
    try {
      const finalConfig = {
        ...this.adjustTimeoutForFormData(data, config)
      };

      // プログレス監視の設定
      if (onProgress && typeof onProgress === 'function') {
        finalConfig.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: percentCompleted
          });
        };
      }

      console.log(`プログレス監視付きPOST: ${endpoint}`, {
        timeout: finalConfig.timeout,
        hasProgressCallback: !!onProgress
      });

      const result = await apiClient.post(endpoint, data, finalConfig);      
      return result;
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'POST_WITH_PROGRESS', 
        endpoint,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // バッチリクエスト用のヘルパー
  static async postBatch(endpoint, data = {}, config = {}, options = {}) {
    const startTime = Date.now();
    const {
      enableProgressLog = true,
      estimatedFileCount = 0,
      onProgress = null
    } = options;
    
    try {
      // バッチリクエスト用の設定調整
      const batchConfig = {
        ...this.adjustTimeoutForFormData(data, config)
      };

      // バッチ処理専用のタイムアウト調整
      if (estimatedFileCount > 5) {
        batchConfig.timeout = Math.min(batchConfig.timeout * 1.5, 300000);
      }

      if (enableProgressLog) {
        console.log(`バッチPOST開始: ${endpoint}`, {
          timeout: batchConfig.timeout,
          estimatedFileCount,
          hasFormData: data instanceof FormData
        });
      }

      // プログレス監視を設定
      if (onProgress) {
        return await this.postWithProgress(endpoint, data, batchConfig, onProgress);
      } else {
        const result = await apiClient.post(endpoint, data, batchConfig);
        
        if (enableProgressLog) {
          const endTime = Date.now();
          console.log(`バッチPOST完了: ${endTime - startTime}ms`);
        }

        return result;
      }
    } catch (error) {
      const errorId = this.handleError(error, { 
        method: 'POST_BATCH', 
        endpoint,
        estimatedFileCount,
        duration: Date.now() - startTime,
        showErrorDialog: config.showErrorDialog
      });
      
      error.errorId = errorId;
      throw error;
    }
  }

  // エラーハンドリング共通
  static handleError(error, context = {}) {
    const { response, code, message, config } = error;
    
    // エラーIDを生成
    const errorId = doTrackingError(error, {
      ...context,
      endpoint: config?.url,
      statusCode: response?.status,
      errorCode: code,
      errorMessage: message,
      requestTimeout: config?.timeout
    });
    
    if (response) {
      console.error(`API Error: ${response.status}`, { 
        errorId, 
        responseData: response.data,
        context 
      });
      
      // タイムアウトエラーの特別処理
      if (response.status === 408 || code === 'ECONNABORTED') {
        console.error('API タイムアウト:', {
          errorId,
          url: config?.url,
          timeout: config?.timeout,
          method: config?.method
        });
      }
    } else if (error.request) {
      console.error('No response received', { errorId, request: error.request });
    } else {
      console.error('Request setup error', { errorId, message: error.message });
    }

    // axios特有のタイムアウトエラー
    if (code === 'ECONNABORTED' && message.includes('timeout')) {
      console.error('リクエストタイムアウト:', {
        errorId,
        timeout: config?.timeout,
        url: config?.url
      });
    }
    
    return errorId;
  }
}

export default ApiService;